import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Loader2, Lock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import PlusUpsellModal from '@/components/PlusUpsellModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SectorPeerComparisonModal — Round 102k (Feb 2026).
 *
 * Opens when a user clicks an outlier chip on the Valuation
 * Multiples card. Shows the 5 cheapest same-sector peers ranked
 * by the requested metric so users can see "which names ARE
 * cheap right now" without leaving the current stock modal.
 *
 * Click a peer row → opens THAT stock's modal via the parent's
 * onOpenStock callback. If no callback is provided, falls back
 * to a full-page navigate to /stock/{ticker}.
 *
 * IMPORTANT: This modal must NOT propagate clicks back to any
 * price-chart / stock-modal outside handlers. All chip click
 * handlers upstream must `e.stopPropagation()` before opening
 * this dialog. See ValuationMultiplesCard.jsx wiring.
 */

const METRIC_LABELS = {
  trailing_pe: 'Trailing P/E',
  pe_forward: 'Forward P/E',
  price_to_sales: 'P/S',
  peg_ratio: 'PEG',
  enterprise_to_ebitda: 'EV/EBITDA',
};

const METRIC_UNITS = {
  trailing_pe: '',
  pe_forward: '',
  price_to_sales: '',
  peg_ratio: '',
  enterprise_to_ebitda: '',
};

function fmtMarketCap(cap) {
  if (!cap || cap <= 0) return '—';
  if (cap >= 1e12) return `$${(cap / 1e12).toFixed(1)}T`;
  if (cap >= 1e9) return `$${(cap / 1e9).toFixed(1)}B`;
  if (cap >= 1e6) return `$${(cap / 1e6).toFixed(0)}M`;
  return `$${cap.toFixed(0)}`;
}

export default function SectorPeerComparisonModal({
  open,
  onOpenChange,
  ticker,
  metric,
  sectorLabel,
  currentValue,
  onOpenStock,
}) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [upsellOpen, setUpsellOpen] = useState(false);
  const { user } = useAuth();
  // Round 108z (Feb 2026) — free dividend tier must NOT be able to
  // drill into non-dividend peers via the outlier chip. Same paywall
  // pattern as Insights peer chips + ETF Top Holdings: gold lock,
  // click opens the Plus upsell modal. Admin bypass keeps QA
  // drill-through working. Flip to `user?.role === 'admin' || user?.is_plus`
  // when the billing flag lands.
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!open || !ticker || !metric) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`${API}/stocks/${ticker}/sector-peers`, {
      params: { metric, limit: 5 },
      withCredentials: true,
    })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, ticker, metric]);

  const metricLabel = METRIC_LABELS[metric] || metric;
  const peers = data?.peers || [];

  const handlePeerClick = (peerTicker) => {
    onOpenChange(false);
    if (onOpenStock) {
      onOpenStock(peerTicker);
    } else {
      // Fallback — navigate. Use replaceState-style so the stock
      // modal history is preserved.
      window.location.href = `/stock/${peerTicker}`;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        data-testid="sector-peer-comparison-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <span className="text-emerald-600 dark:text-emerald-400">Cheapest {metricLabel}</span>
            <span className="text-muted-foreground/70 text-sm font-normal">in {sectorLabel || data?.sector || 'sector'}</span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            {currentValue != null && (
              <span className="mr-2">
                <span className="text-foreground/80 font-mono font-semibold">{ticker} {metricLabel} = {currentValue.toFixed(2)}</span>
              </span>
            )}
            Peers ranked lowest → highest. Click any row to open its Stock Modal.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-8 text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading peers…
          </div>
        )}

        {error && !loading && (
          <div className="py-4 text-sm text-rose-500" data-testid="peer-modal-error">
            Failed to load peers: {error}
          </div>
        )}

        {!loading && !error && peers.length === 0 && (
          <div className="py-4 text-sm text-muted-foreground" data-testid="peer-modal-empty">
            No same-sector peers with {metricLabel} data.
          </div>
        )}

        {!loading && !error && peers.length > 0 && (
          <div data-testid="peer-modal-list">
            {/* Round P108al (Feb 2026) — column-aligned peer table so
                readers can scan each metric down a single vertical
                axis. Header shows the short metric label only (e.g.
                "P/E" not "Trailing P/E") since the outlier chip that
                opened this modal already carried that context. */}
            <div className="grid grid-cols-[minmax(0,1fr)_72px_72px] gap-3 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground border-b border-border/60">
              <div>Ticker · Type</div>
              <div className="text-right">Div Quality</div>
              <div className="text-right">
                {(metricLabel || '').replace(/^Trailing /i, '')}
              </div>
            </div>
            <div className="space-y-1 mt-1">
              {peers.map((p, i) => {
                const isDivPayer = p.pays_dividend === true;
                const locked = !isDivPayer && !isAdmin;
                return (
                  <button
                    key={p.ticker}
                    type="button"
                    onClick={() => (locked ? setUpsellOpen(true) : handlePeerClick(p.ticker))}
                    className={`w-full grid grid-cols-[minmax(0,1fr)_72px_72px] items-center gap-3 px-3 py-2 rounded-md transition-colors text-left ${
                      locked
                        ? 'hover:bg-amber-500/10 ring-1 ring-amber-500/20 hover:ring-amber-500/40'
                        : 'hover:bg-accent'
                    }`}
                    data-testid={locked ? `peer-row-locked-${p.ticker}` : `peer-row-${p.ticker}`}
                    title={locked
                      ? `${p.ticker} is a non-dividend stock — unlock the full market with DiviDrip Plus`
                      : undefined}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {locked ? (
                        <Lock
                          className="w-3 h-3 text-yellow-600 dark:text-yellow-400 shrink-0"
                          data-testid={`peer-row-lock-icon-${p.ticker}`}
                        />
                      ) : (
                        <span className="text-[10px] font-mono text-muted-foreground/60 w-4 shrink-0">{i + 1}.</span>
                      )}
                      <div className="flex flex-col min-w-0">
                        <span className="font-semibold text-sm truncate">
                          {p.ticker}
                          <span className={`ml-1.5 text-[10px] font-normal ${isDivPayer ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>
                            · {isDivPayer ? 'Dividend' : 'Non-Dividend'}
                          </span>
                        </span>
                        <span className="text-[10px] text-muted-foreground/80 truncate">
                          {p.name} · {fmtMarketCap(p.market_cap)}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      {typeof p.quality_score === 'number' && p.quality_score > 0 ? (
                        <span
                          className={`inline-block text-[11px] font-bold font-mono w-9 text-center rounded px-1 py-0.5 ${
                            p.quality_score >= 70 ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                            : p.quality_score >= 50 ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
                            : 'bg-rose-500/15 text-rose-700 dark:text-rose-300'
                          }`}
                          title={`Dividend Quality Score ${p.quality_score}/100`}
                        >
                          {p.quality_score}
                        </span>
                      ) : (
                        <span className="text-[11px] text-muted-foreground/50">—</span>
                      )}
                    </div>
                    <div className="text-right">
                      <span className="font-mono font-bold text-sm text-emerald-600 dark:text-emerald-400 tabular-nums">
                        {p.value.toFixed(2)}{METRIC_UNITS[metric] || ''}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
            {data?.total_peers_with_data > peers.length && (
              <p className="text-[10px] text-muted-foreground/70 mt-2 text-center">
                Showing 5 of {data.total_peers_with_data} peers with {metricLabel} data.
              </p>
            )}
          </div>
        )}
      </DialogContent>
      <PlusUpsellModal open={upsellOpen} onClose={() => setUpsellOpen(false)} />
    </Dialog>
  );
}
